import React, { useEffect, useRef } from 'react';

interface Ember {
  x: number;
  y: number;
  size: number;
  speed: number;
  drift: number;
  flicker: number;
  hue: number;
}

interface Ash {
  x: number;
  y: number;
  size: number;
  speed: number;
  drift: number;
  opacity: number;
}

const VolcanoBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameId = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reducedMotionQuery =
      typeof window !== 'undefined'
        ? window.matchMedia?.('(prefers-reduced-motion: reduce)')
        : undefined;
    let reducedMotion = reducedMotionQuery?.matches ?? false;

    let embers: Ember[] = [];
    let ash: Ash[] = [];
    let time = 0;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initScene();
      if (reducedMotion) renderStatic();
    };

    function initScene() {
      embers = Array.from({ length: 40 }, () => spawnEmber());
      ash = Array.from({ length: 30 }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 2.5 + 1,
        speed: Math.random() * 0.3 + 0.1,
        drift: (Math.random() - 0.5) * 0.4,
        opacity: Math.random() * 0.4 + 0.15,
      }));
    }

    function craterX() {
      return canvas.width / 2;
    }
    function craterY() {
      return canvas.height * 0.55;
    }

    function spawnEmber(): Ember {
      const spread = canvas.width * 0.12;
      return {
        x: craterX() + (Math.random() - 0.5) * spread,
        y: craterY() + Math.random() * 20,
        size: Math.random() * 3 + 1.5,
        speed: Math.random() * 1.6 + 0.8,
        drift: (Math.random() - 0.5) * 0.8,
        flicker: Math.random() * Math.PI * 2,
        hue: 12 + Math.random() * 30,
      };
    }

    function drawSky() {
      const sky = ctx.createLinearGradient(0, 0, 0, canvas.height);
      sky.addColorStop(0, '#150807');
      sky.addColorStop(0.5, '#2a0d09');
      sky.addColorStop(0.8, '#4a1409');
      sky.addColorStop(1, '#7c2208');
      ctx.fillStyle = sky;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    function drawGlow() {
      const pulse = 0.55 + Math.sin(time * 1.5) * 0.2;
      const glow = ctx.createRadialGradient(
        craterX(),
        craterY(),
        0,
        craterX(),
        craterY(),
        canvas.width * 0.5
      );
      glow.addColorStop(0, `rgba(249, 115, 22, ${0.45 * pulse})`);
      glow.addColorStop(0.4, `rgba(220, 38, 38, ${0.2 * pulse})`);
      glow.addColorStop(1, 'rgba(220, 38, 38, 0)');
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    function drawVolcano() {
      const cx = craterX();
      const baseY = canvas.height;
      const peakY = craterY();
      const halfBase = canvas.width * 0.55;
      const craterHalf = canvas.width * 0.1;

      // Mountain body
      ctx.fillStyle = '#1c0a06';
      ctx.beginPath();
      ctx.moveTo(cx - halfBase, baseY);
      ctx.lineTo(cx - craterHalf, peakY);
      ctx.lineTo(cx + craterHalf, peakY);
      ctx.lineTo(cx + halfBase, baseY);
      ctx.closePath();
      ctx.fill();

      // Glowing crater rim
      const rim = ctx.createLinearGradient(0, peakY - 10, 0, peakY + 14);
      rim.addColorStop(0, 'rgba(254, 215, 170, 0.95)');
      rim.addColorStop(1, 'rgba(220, 38, 38, 0)');
      ctx.fillStyle = rim;
      ctx.fillRect(cx - craterHalf, peakY - 6, craterHalf * 2, 18);

      // Lava streaks down the slopes
      const streakPulse = 0.6 + Math.sin(time * 2) * 0.3;
      ctx.lineWidth = 4;
      ctx.strokeStyle = `rgba(249, 115, 22, ${streakPulse})`;
      ctx.beginPath();
      ctx.moveTo(cx - craterHalf * 0.6, peakY + 4);
      ctx.lineTo(cx - halfBase * 0.45, baseY);
      ctx.moveTo(cx + craterHalf * 0.4, peakY + 4);
      ctx.lineTo(cx + halfBase * 0.4, baseY);
      ctx.stroke();
    }

    function drawEmber(e: Ember) {
      e.flicker += 0.2;
      const alpha = 0.6 + Math.sin(e.flicker) * 0.35;
      const halo = ctx.createRadialGradient(e.x, e.y, 0, e.x, e.y, e.size * 4);
      halo.addColorStop(0, `hsla(${e.hue}, 95%, 60%, ${alpha})`);
      halo.addColorStop(1, `hsla(${e.hue}, 95%, 50%, 0)`);
      ctx.fillStyle = halo;
      ctx.beginPath();
      ctx.arc(e.x, e.y, e.size * 4, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = `hsla(${e.hue + 15}, 100%, 75%, ${alpha})`;
      ctx.beginPath();
      ctx.arc(e.x, e.y, e.size, 0, Math.PI * 2);
      ctx.fill();
    }

    function updateEmber(e: Ember) {
      e.y -= e.speed;
      e.x += e.drift + Math.sin(time * 2 + e.flicker) * 0.5;
      if (e.y < canvas.height * 0.05 || e.x < 0 || e.x > canvas.width) {
        Object.assign(e, spawnEmber());
      }
    }

    function drawAsh(a: Ash) {
      ctx.fillStyle = `rgba(120, 113, 108, ${a.opacity})`;
      ctx.beginPath();
      ctx.arc(a.x, a.y, a.size, 0, Math.PI * 2);
      ctx.fill();
    }

    function updateAsh(a: Ash) {
      a.y += a.speed;
      a.x += a.drift;
      if (a.y > canvas.height) {
        a.y = -10;
        a.x = Math.random() * canvas.width;
      }
    }

    function renderStatic() {
      drawSky();
      drawGlow();
      drawVolcano();
      embers.forEach(drawEmber);
      ash.forEach(drawAsh);
    }

    function animate() {
      if (reducedMotion) return;
      time += 0.01;
      drawSky();
      drawGlow();
      drawVolcano();
      embers.forEach((e) => {
        updateEmber(e);
        drawEmber(e);
      });
      ash.forEach((a) => {
        updateAsh(a);
        drawAsh(a);
      });
      animationFrameId.current = requestAnimationFrame(animate);
    }

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const handleReducedMotionChange = (event: MediaQueryListEvent) => {
      reducedMotion = event.matches;
      cancelAnimationFrame(animationFrameId.current);
      if (reducedMotion) {
        renderStatic();
      } else {
        animate();
      }
    };

    reducedMotionQuery?.addEventListener?.('change', handleReducedMotionChange);

    if (reducedMotion) {
      renderStatic();
    } else {
      animate();
    }

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      reducedMotionQuery?.removeEventListener?.('change', handleReducedMotionChange);
      cancelAnimationFrame(animationFrameId.current);
    };
  }, []);

  return <canvas ref={canvasRef} className="fixed inset-0 pointer-events-none z-0" />;
};

export default VolcanoBackground;
